import { useContext, useEffect, useMemo } from "react";

import { SidebarState, UIContext } from "@alpacahq/ui/old";

import { useScript } from "~/v2/hooks/useScript";
import { useAccountContext } from "~/v2/providers/AccountProvider";
import { useMultiAccountFeatures } from "./useAccountFeatures";

declare global {
  interface Window {
    zE: (...args: string[]) => void;
  }
}

const ZENDESK_SCRIPT =
  "https://static.zdassets.com/ekr/snippet.js?key=d1129a4a-e01a-41e4-a34c-0a596cc59cad";

const useZendeskSupport = () => {
  const { liveAccounts } = useAccountContext();
  const { sidebarState } = useContext(UIContext);

  // Get account IDs for all live accounts
  const accountIds = useMemo(
    () => (liveAccounts || []).map((a) => a.id),
    [liveAccounts],
  );

  // Fetch features for all live accounts
  const { featuresMap } = useMultiAccountFeatures(accountIds);

  // Check if Zendesk should be enabled
  const enabled = useMemo(() => {
    if (!liveAccounts || liveAccounts.length === 0) {
      return false;
    }

    // Check if any account has elite status
    const hasEliteStatus = liveAccounts.some(
      (a) => a.elite_status === "100k" || a.elite_status === "30k",
    );

    if (hasEliteStatus) {
      return true;
    }

    // Check if any account has zendesk_chatbot feature enabled
    return Object.values(featuresMap).some((f) => f.zendesk_chatbot);
  }, [liveAccounts, featuresMap]);

  useScript(ZENDESK_SCRIPT, {
    shouldPreventLoad: !enabled,
    removeOnUnmount: true,
    id: "ze-snippet",
  });

  /*
    The chat widget overlaps our mobile sidebar's Close button, so we have to
    hide it.

    TODO: If we revisit the sidebar's layout, look at removing this.
  */
  useEffect(() => {
    if (sidebarState === SidebarState.HIDDEN) {
      window.zE?.("messenger", "show");
    } else if (sidebarState === SidebarState.MOBILE) {
      window.zE?.("messenger", "hide");
    }
  }, [sidebarState]);
};

export default useZendeskSupport;
